import { Image, useDeviceType } from '@cian/ui-kit';
import React, { useEffect } from 'react';
import styles from './styles.css';
import cn from 'clsx';
import { PhoneForm, MortgageForm, Success } from './components';
import { useRefinLeadBanner } from './hooks';
import { IRefinLeadBannerProps } from './types';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useTracking } from '../../utils/tracking/useTracking';

const BANNER_IMAGE_PATH =
  'https://static.cdn-cian.ru/frontend/mortgage-assets/mortgage_promo_frontend/banners/lowRatePackage.png';

export const RefinLeadBanner: React.FC<IRefinLeadBannerProps> = props => {
  const deviceType = useDeviceType();
  const { trackScrollToLeadForm } = useTracking();
  const { elementRef, hasIntersected } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.1,
    rootMargin: '0px',
    triggerOnce: true,
  });

  const {
    error,
    lead,
    isLeadSending,
    isLeadSuccessfullySent,
    isRefinLeadSending,
    isRefinLeadSuccessfullySent,
    onPhoneChange,
    onMortgageAmountRemainingChange,
    onMortgageRateChange,
    onAgreeToGetAdsChange,
    onHasNoMortgageClick,
    handleMortgageFormSubmit,
    handlePhoneFormSubmit,
  } = useRefinLeadBanner(props);

  const isAllSubmitted = isLeadSuccessfullySent && isRefinLeadSuccessfullySent;

  // Отслеживаем появление баннера на экране
  useEffect(() => {
    if (hasIntersected) {
      trackScrollToLeadForm();
    }
  }, [hasIntersected, trackScrollToLeadForm]);

  const renderFormContent = () => {
    if (isLeadSuccessfullySent && isRefinLeadSuccessfullySent) {
      return <Success withMortgage={lead.hasMortgage} />;
    }

    if (!isLeadSuccessfullySent) {
      return (
        <PhoneForm
          phone={lead.phone}
          onPhoneChange={onPhoneChange}
          onAgreeToGetAdsChange={onAgreeToGetAdsChange}
          agreeToGetAds={lead.agreeToGetAds}
          onSubmit={handlePhoneFormSubmit}
          error={error}
          isLoading={isLeadSending}
        />
      );
    }

    return (
      <MortgageForm
        mortgageAmountRemaining={lead.mortgageAmountRemaining}
        mortgageRate={lead.mortgageRate}
        onMortgageAmountRemainingChange={onMortgageAmountRemainingChange}
        onMortgageRateChange={onMortgageRateChange}
        onHasNoMortgageClick={onHasNoMortgageClick}
        onWithMortgageSubmit={handleMortgageFormSubmit}
        isLoading={isRefinLeadSending}
        error={error}
      />
    );
  };

  const isRefin = isLeadSuccessfullySent && !isRefinLeadSuccessfullySent;

  return (
    <div
      ref={elementRef}
      className={cn(
        styles['container'],
        styles[deviceType],
        { [styles['refin']]: isRefin },
        { [styles['success']]: isAllSubmitted },
      )}
    >
      <div
        data-testid="lead-form"
        className={cn(
          styles['form'],
          styles[deviceType],
          { [styles['form-mortgage']]: isRefin },
          { [styles['form-success']]: isAllSubmitted },
        )}
      >
        {renderFormContent()}
      </div>

      <div
        className={cn(
          styles['image'],
          styles[deviceType],
          { [styles['medium']]: isAllSubmitted },
          { [styles['refin']]: isRefin },
        )}
      >
        <Image src={BANNER_IMAGE_PATH} alt="Посылка со сниженными ставками" />
      </div>
    </div>
  );
};
